import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, RotateCcw, Save, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function GiftCardBuilder() {
  const { language } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const id = language === 'id';
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    front_title: '', front_subtitle: '', back_message: '',
    recipient_name: '', sender_name: ''
  });

  useEffect(() => {
    axios.get(`${API}/gift-card-templates`).then(r => {
      const list = r.data.templates || [];
      setTemplates(list);
      if (list.length > 0) setSelectedTemplate(list[0]);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!user) { login(); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/gift-cards`, { ...form, template_id: selectedTemplate?.template_id }, { withCredentials: true });
      toast.success(id ? 'Kartu hadiah disimpan' : 'Gift card saved');
      navigate('/memories');
    } catch { toast.error(id ? 'Gagal menyimpan' : 'Failed to save'); }
    setSaving(false);
  };

  if (!selectedTemplate) {
    return (
      <ImmersiveLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-seikat-pink" />
        </div>
      </ImmersiveLayout>
    );
  }

  const cardBg = selectedTemplate.background || selectedTemplate.image || 'linear-gradient(135deg, #1A1413 0%, #120E0D 100%)';

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="gift-card-builder">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs tracking-widest uppercase text-seikat-bone-muted hover:text-seikat-pink transition-colors mb-10" data-testid="back-button">
            <ArrowLeft size={14} /> {id ? 'Kembali' : 'Back'}
          </button>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-lime" />
              <span className="eyebrow-immersive">{id ? 'Kartu Hadiah' : 'Gift Card'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Tulis <em>pesan</em> yang tak terlupakan.</> : <>Write an <em>unforgettable</em> note.</>}
            </h1>
          </motion.div>

          <div className="mt-14 grid lg:grid-cols-[1fr_440px] gap-10">
            {/* Preview */}
            <div>
              <div className="glass-dark rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <p className="eyebrow-immersive">{id ? 'Pratinjau' : 'Preview'}</p>
                  <button
                    onClick={() => setShowBack(s => !s)}
                    className="btn-pill text-[10px] px-4 py-2"
                    data-testid="flip-card-btn"
                  >
                    <RotateCcw size={11} /> {showBack ? (id ? 'Depan' : 'Front') : (id ? 'Belakang' : 'Back')}
                  </button>
                </div>
                <motion.div
                  key={showBack ? 'back' : 'front'}
                  initial={{ rotateY: -90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="relative aspect-[3/2] rounded-2xl overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)]"
                  style={{ background: cardBg.startsWith('linear') || cardBg.startsWith('#') ? cardBg : `url(${cardBg}) center/cover`, perspective: 800 }}
                >
                  <div className="absolute inset-0 bg-seikat-ink/30" />
                  {showBack ? (
                    <div className="absolute inset-0 p-10 flex flex-col justify-between text-seikat-bone">
                      <div>
                        {form.recipient_name && (
                          <p className="font-body text-xs tracking-widest uppercase opacity-70">{id ? 'Untuk' : 'For'}</p>
                        )}
                        <p className="font-heading text-3xl mt-1">{form.recipient_name || (id ? 'Penerima' : 'Recipient')}</p>
                      </div>
                      <p className="font-heading italic text-xl leading-relaxed flex-1 mt-4 line-clamp-6">
                        {form.back_message || (id ? 'Tulis pesan personalmu di sini…' : 'Write your personal message here…')}
                      </p>
                      {form.sender_name && (
                        <p className="font-body text-sm opacity-80 mt-4">— {form.sender_name}</p>
                      )}
                    </div>
                  ) : (
                    <div className="absolute inset-0 p-10 flex flex-col items-center justify-center text-center text-seikat-bone">
                      <Sparkles size={20} className="text-seikat-pink mb-4" strokeWidth={1.4} />
                      <p className="font-heading text-4xl">{form.front_title || (id ? 'Untukmu' : 'For You')}</p>
                      <p className="font-heading italic text-lg mt-2 opacity-80">{form.front_subtitle || (id ? 'Dengan cinta' : 'With love')}</p>
                      <div className="absolute bottom-6 left-0 right-0 text-center">
                        <p className="text-[10px] tracking-[0.4em] uppercase opacity-60">Seikat · Atelier</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Template picker */}
              {templates.length > 1 && (
                <div className="glass-dark rounded-2xl p-6 mt-6">
                  <p className="eyebrow-immersive mb-4">{id ? 'Template' : 'Template'}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {templates.map(tpl => (
                      <button
                        key={tpl.template_id}
                        onClick={() => setSelectedTemplate(tpl)}
                        data-testid={`gift-template-${tpl.template_id}`}
                        className={`aspect-[3/2] rounded-lg overflow-hidden border-2 transition-all ${selectedTemplate?.template_id === tpl.template_id ? 'border-seikat-pink' : 'border-white/8 hover:border-white/25'}`}
                        style={{ background: (tpl.background || tpl.image || '#1A1413').startsWith('linear') || (tpl.background || tpl.image || '#1A1413').startsWith('#') ? (tpl.background || '#1A1413') : `url(${tpl.background || tpl.image}) center/cover` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Form */}
            <div className="glass-dark rounded-2xl p-7 lg:sticky lg:top-10 h-fit">
              <p className="eyebrow-immersive">{id ? 'Komposisi' : 'Compose'}</p>
              <h2 className="font-heading text-3xl text-seikat-bone mt-1 mb-6">{id ? 'Pesanmu' : 'Your Message'}</h2>
              <div className="space-y-4">
                <Field label={id ? 'Judul Depan' : 'Front Title'}>
                  <input value={form.front_title} onChange={e => setForm({ ...form, front_title: e.target.value })} placeholder={id ? 'Untukmu' : 'For You'} className="immersive-input" data-testid="gc-front-title" />
                </Field>
                <Field label={id ? 'Subjudul Depan' : 'Front Subtitle'}>
                  <input value={form.front_subtitle} onChange={e => setForm({ ...form, front_subtitle: e.target.value })} placeholder={id ? 'Dengan cinta' : 'With love'} className="immersive-input" data-testid="gc-front-subtitle" />
                </Field>
                <Field label={id ? 'Untuk (Nama)' : 'To (Name)'}>
                  <input value={form.recipient_name} onChange={e => setForm({ ...form, recipient_name: e.target.value })} className="immersive-input" data-testid="gc-recipient" />
                </Field>
                <Field label={id ? 'Dari (Nama)' : 'From (Name)'}>
                  <input value={form.sender_name} onChange={e => setForm({ ...form, sender_name: e.target.value })} className="immersive-input" data-testid="gc-sender" />
                </Field>
                <Field label={id ? 'Pesan Personal' : 'Personal Message'}>
                  <textarea
                    value={form.back_message}
                    onChange={e => setForm({ ...form, back_message: e.target.value })}
                    placeholder={id ? 'Tulis kata-kata yang akan diingat…' : 'Write words to be remembered…'}
                    rows={4}
                    className="immersive-input resize-none"
                    data-testid="gc-message"
                  />
                </Field>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-pill btn-pill--solid w-full justify-center mt-3"
                  data-testid="gc-save-btn"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {id ? 'Simpan Kartu' : 'Save Card'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">{label}</div>
      {children}
    </label>
  );
}
